import { Flame, TrendingDown, MessageCircle, MapPin } from "lucide-react";
import { CONTACT } from "../data/site";
import { PageHero } from "../components/PageHero";
import { LeadForm } from "../components/LeadForm";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { Stagger, Item, Reveal } from "../components/shared";
import { useI18n } from "../i18n/LanguageContext";
import { useDeals } from "../hooks/useDeals";

const DealCard = ({ d, enquire }) => (
  <Item>
    <div className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-card" data-testid={`distress-card-${d.id}`}>
      <div className="relative aspect-[16/11] overflow-hidden">
        <img src={d.image} alt={d.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" />
        <span className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-red-500 px-3 py-1.5 text-sm font-semibold text-white shadow-lg"><TrendingDown size={15} /> −{d.drop}</span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center gap-1.5 text-muted"><MapPin size={14} className="text-gold" /><span className="text-sm">{d.location} · {d.developer}</span></div>
        <h3 className="mt-2 font-display text-2xl font-semibold tracking-tight text-navy">{d.name}</h3>
        <p className="mt-1 text-sm text-muted">{d.beds}</p>
        <p className="mt-3 text-sm leading-relaxed text-muted">{d.description}</p>
        <div className="mt-5 flex items-end gap-3"><span className="text-sm text-muted line-through decoration-red-400">{d.oldPrice} {d.currency}</span></div>
        <span className="font-display text-2xl font-semibold text-navy">{d.newPrice} <span className="text-sm font-medium text-muted">{d.currency}</span></span>
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-sand px-3 py-2.5"><Flame size={14} className="shrink-0 text-red-500" /><span className="text-xs font-medium text-navy/80">{d.reason}</span></div>
        <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid={`distress-enquire-${d.id}`} className="btn-primary mt-6 w-full"><MessageCircle size={16} /> {enquire}</a>
      </div>
    </div>
  </Item>
);

export default function DistressPage() {
  const { t, c } = useI18n();
  const p = t.distressPage;
  const items = useDeals(c.DISTRESS_DEALS);
  return (
    <div data-testid="distress-page">
      <Seo path="/distress" title={t.seo.distress.title} description={t.seo.distress.desc} image={c.DISTRESS_DEALS[0].image}
        jsonLd={breadcrumbJsonLd([{ name: t.crumbHome, path: "/" }, { name: p.crumb, path: "/distress" }])} />
      <PageHero eyebrow={p.eyebrow} crumb={p.crumb} title={p.title} sub={p.sub} image={c.DISTRESS_DEALS[0].image} />
      <section className="py-16 sm:py-24">
        <div className="container-x">
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((d) => <DealCard key={d.id} d={d} enquire={t.distress.enquire} />)}
          </Stagger>
        </div>
      </section>
      <section className="pb-24" data-testid="distress-lead">
        <div className="container-x">
          <div className="grid items-center gap-10 rounded-[2rem] bg-navy p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <span className="eyebrow">{p.eaEyebrow}</span>
              <h2 className="mt-5 font-display text-3xl font-semibold leading-tight text-white sm:text-4xl">{p.eaTitle1} <span className="text-gold-300">{p.eaAccent}</span></h2>
              <Reveal><p className="mt-5 text-base leading-relaxed text-white/70">{p.eaSub}</p></Reveal>
            </div>
            <LeadForm interest="Distress deals early access" />
          </div>
        </div>
      </section>
    </div>
  );
}
